import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

export const CSS_HIDE_MENU = 'd-none';

@Directive({
    selector: '[appMenuTriggerFor]',
})
export class MenuTriggerForDirective implements OnInit, OnDestroy {
    @Input({ required: true, alias: 'appMenuTriggerFor' })
    menuElement!: HTMLElement;

    private triggerSubscription?: Subscription;
    private documentSubscription?: Subscription;

    private document = inject<Document>(DOCUMENT);
    private triggerElm: HTMLElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    ngOnInit(): void {
        // if menu is shown by default
        if (!this.menuElement.classList.contains(CSS_HIDE_MENU)) {
            this.initClickout();
        } else {
            this.initTrigger();
        }
    }

    ngOnDestroy(): void {
        if (this.documentSubscription) {
            this.documentSubscription.unsubscribe();
        }

        if (this.triggerSubscription) {
            this.triggerSubscription.unsubscribe();
        }
    }

    private showMenu() {
        this.menuElement.classList.remove(CSS_HIDE_MENU);
        this.triggerSubscription?.unsubscribe();
        this.initClickout();
    }

    private hideMenu() {
        this.menuElement.classList.add(CSS_HIDE_MENU);
        this.documentSubscription?.unsubscribe();
        this.initTrigger();
    }

    private initClickout() {
        this.documentSubscription = merge(
            fromEvent(this.document, 'click'),
            fromEvent<KeyboardEvent>(this.document, 'keyup').pipe(filter((e: KeyboardEvent) => e.code === 'Escape')),
        ).subscribe(({ target }: Event) => {
            if (!target || !this.isTarget(this.menuElement, target)) {
                this.hideMenu();
            }
        });
    }

    private initTrigger() {
        this.triggerSubscription = fromEvent(this.triggerElm, 'click').subscribe((e) => {
            // by preventing bubbling we avoid the click event on whole document
            e.stopPropagation();
            this.showMenu();
        });
    }

    private isTarget(source: HTMLElement, target: EventTarget | HTMLElement) {
        return source === target || source.contains(target as HTMLElement);
    }
}
